import type { I18nHeadInput } from '@i18n-micro/types'
import type { RouteLocationAsPathGeneric, RouteLocationAsRelativeGeneric } from 'vue-router'
import type {
  Locale,
  AddressDisplayRow,
  PersonalInfoField
} from '~/types/locale'
import {
  CountryCode,
  StampinupDomain,
  Culture,
  StampinupDomainEnvPrefix,
  LanguagePath
} from '~/types/locale'
import { Env } from '~/types/environment'
import { getEnumKeyByEnumValue } from '~/utils/general'

const nonDefaultLanguagePaths = [...new Set(
  Object.values(LanguagePath).filter(path => path.length > 0)
)]
/**
 * Get back the correct domain name by the locale and language
 *
 * @export
 * @param {Culture} culture
 * @returns
 */
export function getLanguagePath (culture: Culture | null) {
  const key = getEnumKeyByEnumValue(Culture, culture)
  return key
    ? LanguagePath[key]
    : null
}

export function getLanguagePathFromRoute (path: string) {
  return nonDefaultLanguagePaths.find(languagePath =>
    path === languagePath || path.startsWith(`${languagePath}/`)
  ) ?? ''
}

export function languagesForDomain (domain: StampinupDomain) {
  const cultures: Array<keyof typeof StampinupDomain> = []
  const languages = []
  Object.keys(StampinupDomain).forEach((key) => {
    const keyDomain = key as keyof typeof StampinupDomain
    if (StampinupDomain[keyDomain] === domain) {
      cultures.push(keyDomain)
    }
  })
  for (const cultureKey of cultures) {
    const langPath = LanguagePath[cultureKey as keyof typeof LanguagePath]
    if (langPath !== '') {
      languages.push(langPath.substring(1))
    }
  }
  return languages
}

/**
 * Get back the correct domain name by the locale and language
 *
 * @export
 * @param {CountryCode} culture
 * @returns
 */
export function getStampinupDomain (culture: Culture | null) {
  const key = getEnumKeyByEnumValue(Culture, culture)
  return key
    ? StampinupDomain[key]
    : null
}

/**
 * Get back the env-prefix for domain
 *
 * @export
 * @param {Env} env
 * @returns
 */
export function getStampinupDomainEnvPrefix (env: Env | null) {
  if (env === Env.Local) {
    return StampinupDomainEnvPrefix.Local
  }
  if (env === Env.Development) {
    return StampinupDomainEnvPrefix.DEV
  }
  if (env === Env.Testing) {
    return StampinupDomainEnvPrefix.QA
  }
  if (env === Env.Release) {
    return StampinupDomainEnvPrefix.Release
  }
  return StampinupDomainEnvPrefix.Non
}

/**
 * Get back the api domain
 *
 * @param {string} prefix
 * @param {Culture} culture
 * @returns
 */
export function getStampinupApiDomain (prefix: string, culture: Culture, port?: string | number) {
  const domain = getStampinupDomain(culture)
  return port ? `https://${prefix}.${domain}:${port}` : `https://${prefix}.${domain}`
}

/**
 * Get back domain from HTTP Host header
 *
 * @export
 * @param {string} hostHeader
 * @returns
 */
export function getStampinupDomainFromHost (hostHeader: string) {
  const hostName = hostHeader.split(':')[0] ?? hostHeader
  const domainFound = Object
    .values(StampinupDomain)
    .find(domain => hostName.endsWith(domain))
  return domainFound || null
}

type RequestHeaders = Record<string, string | string[] | undefined>

const firstHeader = (value: string | string[] | undefined): string | undefined =>
  Array.isArray(value) ? value[0] : value

/**
 * The host a request was made to: the proxy's `sthost` first, then `host`, then
 * the HTTP/2 `:authority` pseudo-header (an HTTP/2 request has no Host header;
 * nuxt2 read `:authority` too). Every server-side host lookup goes through here
 * so the locale and the cookie scope cannot resolve different hosts.
 */
export function getRequestHost (headers: RequestHeaders): string | undefined {
  return firstHeader(headers.sthost)
    ?? firstHeader(headers.host)
    ?? firstHeader(headers[':authority'])
}

/**
 * Strip a sub-locale language prefix (e.g. "/fr", "/de") from a path.
 *
 * On sub-locale domains the lang-guard middleware prefixes in-app paths with
 * the language segment, so route guards that match on the path must compare
 * against the unprefixed form. Only strips when the segment is followed by
 * another segment (e.g. "/fr/join" -> "/join"); a bare "/fr" is left as-is.
 */
export function stripSubLocalePrefix (path: string, subLocaleLang?: string | null): string {
  return subLocaleLang && path.startsWith(`/${subLocaleLang}/`)
    ? path.slice(subLocaleLang.length + 1)
    : path
}

/**
 * Whether a path already carries the sub-locale language segment: "/fr" itself or
 * anything under "/fr/". A path that merely starts with the letters ("/fresh…",
 * "/demonstrator" on a /de domain) does not.
 */
export function hasSubLocalePrefix (path: string, subLocaleLang?: string | null): boolean {
  return !!subLocaleLang && (path === `/${subLocaleLang}` || path.startsWith(`/${subLocaleLang}/`))
}

/**
 * Absolute site URL for an in-app path, with the language path exactly once.
 *
 * `baseUrl` (the locale store's) ends with the culture's language path ("/fr", "/de") on the
 * markets that have one. A path from `router.resolve` by name already carries that segment
 * too, because makeLangRoutes prefixes the path but keeps the route name; nuxt2 renamed the
 * prefixed copies to `lang-<name>`, so the same lookup there gave the bare path. Gluing the
 * two gave "/fr/fr/shared-lists/<id>" on BE_FR, BE_DE and CA_FR (Bug 65113). A path without
 * the segment still gets it from `baseUrl`, and markets with no language path are unchanged.
 */
export function toSiteUrl (baseUrl: string, path: string): string {
  const trimmedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  const languagePath = nonDefaultLanguagePaths.find(lp => trimmedBase.endsWith(lp))
  if (languagePath && hasSubLocalePrefix(path, languagePath.slice(1))) {
    return `${trimmedBase.slice(0, -languagePath.length)}${path}`
  }
  return `${baseUrl}${path}`
}

export type LocalizableLocation = string | RouteLocationAsRelativeGeneric | RouteLocationAsPathGeneric

/**
 * Add the sub-locale language segment to a router location, once. Only an
 * app-relative path takes it: a string or a `{ path }` location starting with
 * "/". An absolute URL (a cross-domain culture switch), a relative path and a
 * named location (makeLangRoutes already gives it the prefixed path) are left alone.
 *
 * Every link href gets this through the sub-locale-links plugin's `router.resolve`
 * wrapper; useLocalizedTo applies it to a `router.push` or `window` target. A
 * `{ path }` location takes the prefix too (useRedirect builds one): left bare, it
 * relied on lang-guard, and the home page's own redirect to '/' never matched
 * '/fr/' (AB#63988). The language-root check is on the whole string, so '/fr?x'
 * would become '/fr/fr?x'; no content route has that shape (every QA content
 * route is stored without the language segment, checked 2026-10-19). AB#64915
 */
export function localizeLocation (location: string, subLocaleLang?: string | null): string
export function localizeLocation (location: LocalizableLocation, subLocaleLang?: string | null): LocalizableLocation
export function localizeLocation (location: LocalizableLocation | undefined, subLocaleLang?: string | null): LocalizableLocation | undefined
export function localizeLocation (location: LocalizableLocation | undefined, subLocaleLang?: string | null): LocalizableLocation | undefined {
  const needsPrefix = (path: string) => !!subLocaleLang && path.startsWith('/') && !hasSubLocalePrefix(path, subLocaleLang)
  if (typeof location === 'string') {
    return needsPrefix(location) ? `/${subLocaleLang}${location}` : location
  }
  if (location && 'path' in location && typeof location.path === 'string' && needsPrefix(location.path)) {
    return { ...location, path: `/${subLocaleLang}${location.path}` }
  }
  return location
}

/**
 * Get back localization from domain
 *
 * @export
 * @param {StampinupDomain} stampinupDomain
 * @returns
 */
export function getLocaleFromStampinupDomain (stampinupDomain: StampinupDomain): Locale | null {
  const key = getEnumKeyByEnumValue(StampinupDomain, stampinupDomain)
  if (key) {
    return {
      countryCode: CountryCode[key.split('_')[1] as keyof typeof CountryCode],
      culture: Culture[key]
    }
  }
  return null
}

/**
 * Get back demo is valid for domain
 *
 * @export
 * @param {Culture} culture
 * @param {number} demoId
 * @returns
 */
export function getIsValidDemoForCulture (culture: Culture, demoId: number): boolean {
  // valid NA demonstrators
  if ((demoId > 0 && demoId < 3000000) && (culture === Culture.EN_US || culture === Culture.EN_CA || culture === Culture.FR_CA)) {
    return true
  }
  // valid SP demonstrators
  if ((demoId >= 4000000 && demoId < 5000000) && (culture === Culture.EN_AU || culture === Culture.EN_NZ)) {
    return true
  }
  // valid EU demonstrators
  if ((demoId >= 5000000 && demoId < 7000000) &&
    (culture === Culture.DE_AT || culture === Culture.DE_DE || culture === Culture.EN_GB || culture === Culture.FR_FR || culture === Culture.NL_NL || culture === Culture.EN_IE || culture === Culture.DE_BE || culture === Culture.FR_BE || culture === Culture.NL_BE)) {
    return true
  }
  return false
}

export function normalizeAddressDisplayConfig (displayConfig: AddressDisplayRow[]) {
  return displayConfig.map(el =>
    typeof el === 'string'
      ? [el]
      : el
  )
}


/**
 * Clean-up applied on top of nuxt-i18n-micro's SEO head, as a `useI18nHead` override.
 *
 * Nuxt2 post-processed the MetaInfo from `$nuxtI18nHead`; in Nuxt4 the module's meta plugin emits
 * the tags itself, so the same rules are expressed declaratively and merged by that plugin:
 * - drop og:locale:alternate. When Facebook needs an object in another locale it re-requests the
 *   URL with `fb_locale` set and expects the markup back in that locale — which this site does
 *   not do (culture is fixed by domain), so advertising alternates is wrong.
 *   https://developers.facebook.com/docs/beta/opengraph/internationalization/
 * - drop the canonical and the module's `og:url` when the page emits its own (`filterCanonical`).
 *   `disable: ['canonical']` does not touch og:url, and unhead keeps whichever og:url entry was
 *   registered last, so leaving the module's in place made the page's win only by ordering — and
 *   left a stray route-path og:url on a product with no slug, which emits neither tag (AB#62347).
 * - pin `dir` to `ltr`. nuxt-i18n defaulted it to ltr; i18n-micro falls back to `auto` for locales
 *   without a `dir`, and no supported culture is right-to-left.
 * - pin the canonical and `og:url` to `canonicalUrl`, which callers build from the route path
 *   alone. Nuxt2's `addCanonicalLinks` used `currentRoute.path` with no query handling at all, so
 *   no query param belongs in either tag; both are set from the same value so they cannot
 *   disagree. (`canonicalQueryWhitelist: []` in nuxt.config keeps the module's own values
 *   query-free for layouts that do not come through here.)
 */
export function cleanI18nMeta (canonicalUrl?: string, filterCanonical?: boolean): I18nHeadInput {
  if (filterCanonical) {
    // The page owns both tags in this case — products set their own canonical and og:url.
    return {
      htmlAttrs: { dir: 'ltr' },
      disable: ['og-alternates', 'canonical'],
      replace: { ogUrl: false }
    }
  }
  return {
    htmlAttrs: { dir: 'ltr' },
    disable: ['og-alternates'],
    ...(canonicalUrl ? { replace: { canonical: canonicalUrl, ogUrl: canonicalUrl } } : {})
  }
}

// Cultures that write cents after a comma ("9,50"); everywhere else it is a
// point. Shared by the currency keydown filter and the amount validation rules,
// which must agree on the one separator a shopper may enter.
const COMMA_CENTS_CULTURES: readonly Culture[] = [
  Culture.DE_AT, Culture.DE_DE, Culture.DE_BE, Culture.FR_BE, Culture.FR_CA, Culture.FR_FR, Culture.NL_BE, Culture.NL_NL
]

export const usesCommaCents = (culture: Culture | null | undefined): boolean =>
  !!culture && COMMA_CENTS_CULTURES.includes(culture)

// Whether a personal-info field is unconditionally required, in either form the config writes
// rules in: `{ required: true }` or a vee-validate string such as 'required|su_digits:9'.
// `required_if:` and `su_required_object` are not counted. AB#64917
export const isFieldRequired = (field: PersonalInfoField): boolean =>
  typeof field.rules === 'string'
    ? field.rules.split('|').some(rule => rule.trim() === 'required')
    : field.rules?.required === true
